<template>
  <v-app-bar :elevation="0" density="compact" class="standalone-topbar">
    <!-- ── Where am I ────────────────────────────────────────────────────
         The app's identity, at the far left of the bar — the first thing
         read on every screen, and the one place it appears (the sidebar
         below carries destinations only). The open section is the lit
         sidebar row; this bar never repeats it. -->
    <div class="sh-brand">
      <button
        v-if="mobile"
        type="button"
        class="sh-nav-toggle"
        :aria-expanded="navOpen ? 'true' : 'false'"
        :aria-label="t('nav.toggle')"
        @click="toggleNav"
      >
        <Transition name="sh-icon-swap" mode="out-in">
          <v-icon :key="navOpen ? 'close' : 'menu'" size="20">{{ navOpen ? "close" : "menu" }}</v-icon>
        </Transition>
      </button>
      <span class="sh-brand__mark">
        <LogoTile :branding="branding" :alt="brandName" :size="26" />
      </span>
      <span class="sh-brand__id">
        <span class="sh-brand__name">{{ brandName }}</span>
        <!-- PRODUCT apps carry the Metaship identity; the app itself (CRM,
             WMS) is the qualifier beside it. A white-label app has no
             subtitle — its name IS the brand. -->
        <span v-if="brandSubtitle" class="sh-brand__sub">{{ brandSubtitle }}</span>
      </span>
      <!-- One glyph that TURNS rather than two that swap: the chevron points
           at what the click will do (collapse ← / expand →). -->
      <button
        v-if="!mobile"
        type="button"
        class="sh-collapse"
        :class="{ 'sh-collapse--rail': rail }"
        :title="rail ? t('nav.expand') : t('nav.collapse')"
        :aria-label="rail ? t('nav.expand') : t('nav.collapse')"
        @click="toggleRail"
      >
        <v-icon size="18" class="sh-collapse__icon">chevron_left</v-icon>
      </button>
    </div>

    <v-spacer />

    <!-- ── Module actions (DEV-923) ──────────────────────────────────────
         The right of the bar belongs to the HOSTED MODULE, not the shell: a
         module teleports its own bar-level controls (theBoer's notification
         bell) into this target. The account controls that used to live here
         — organisation, theme, user — are the sidebar's footer now, pinned
         under the last destination. The id is a published contract: a module
         that finds no element by this id is in another host and renders its
         controls inline instead. -->
    <div id="metaship-topbar-actions" class="sh-actions"></div>
  </v-app-bar>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import { useDisplay } from "vuetify";
import { useSessionStore } from "@/stores/session";
import { hasOwnIdentity } from "@/tenant/branding";
import LogoTile from "./LogoTile.vue";
import { navOpen, rail, toggleNav, toggleRail } from "./navState";

export default defineComponent({
  name: "TopBar",
  components: { LogoTile },
  setup() {
    const { t } = useI18n();
    const session = useSessionStore();
    // `mobileBreakpoint: 960` — plugins/vuetify.ts.
    const { mobile } = useDisplay();

    const branding = computed(() => session.tenant?.branding ?? null);
    /**
     * A PRODUCT app is one of OUR products on our own domain (crm.metaship.ai,
     * wms.metaship.ai) — it wears the Metaship name, with the product as the
     * qualifier. An app with its OWN identity (every WHITE_LABEL app, and a
     * PRODUCT app whose branding says so — Boer, DEV-925) stands under its
     * own name and mark alone and never shows ours.
     */
    const ownIdentity = computed(() => hasOwnIdentity(session.tenant?.kind, session.tenant?.branding));
    const brandName = computed(() => (ownIdentity.value ? session.tenant?.name || "Metaship" : "Metaship"));
    const brandSubtitle = computed(() => (ownIdentity.value ? null : session.tenant?.name || null));

    return {
      t,
      mobile,
      rail,
      navOpen,
      toggleRail,
      toggleNav,
      branding,
      brandName,
      brandSubtitle,
    };
  },
});
</script>

<style scoped>
/* Full-bleed: the brand block at the left now sits ABOVE the sidebar rather
   than beside it, so the bar no longer indents by the nav width (and no
   longer re-flows every time the rail collapses). */
.standalone-topbar {
  position: relative;
  /* BOE-62 — in an installed iPhone app on the dark theme the status bar is
     translucent and the web view starts UNDER it (index.html STATUS-BAR
     CONTRACT). The bar pays that inset itself, so its colour runs up behind
     the clock and its 48px of content sit below it. env() is 0 everywhere
     else. The height is the published contract, not a second copy of it. */
  padding-top: env(safe-area-inset-top, 0px);
  height: var(--metaship-header-height) !important;
  background: var(--sh-chrome-bg) !important;
  /* An inset shadow, NOT `border-bottom`: a border adds an extra pixel, and the
     bar's height is a published contract (`--metaship-header-height`, 48px +
     the top inset) that every hosted module pads itself by. A 1px drift there
     puts the top of each module's content under the bar. */
  box-shadow: inset 0 -1px 0 var(--sh-hairline) !important;
}
.standalone-topbar :deep(.v-toolbar__content) {
  position: relative;
  z-index: 1;
  padding-left: 10px;
  padding-right: 8px;
  gap: 6px;
}
/* A whisper of the tenant's own colour behind the brand block — the bar
   is tinted, not painted, so a branded navbar colour never fights the
   module below it. Drifts very slowly so the chrome feels alive. */
.standalone-topbar::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 0; /* below the toolbar content, which Vuetify positions itself */
  pointer-events: none;
  background:
    radial-gradient(60% 140% at 0% 50%, rgba(var(--v-theme-primary), 0.14), transparent 70%),
    radial-gradient(30% 120% at 28% 50%, rgba(var(--v-theme-secondary), 0.08), transparent 70%);
  animation: sh-aurora 14s ease-in-out infinite alternate;
}
@keyframes sh-aurora {
  from {
    opacity: 0.7;
    transform: translateX(0);
  }
  to {
    opacity: 1;
    transform: translateX(24px);
  }
}

/* ── Brand ─────────────────────────────────────────────────────────────── */
.sh-brand {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
  flex: 0 1 auto;
}
/* The mark lifts a touch under the pointer — the brand is a thing you can
   hold, not a static stamp. */
.sh-brand__mark {
  display: inline-flex;
  flex: 0 0 auto;
  border-radius: 8px;
  transition:
    transform var(--sh-motion-slow) var(--sh-ease-spring),
    box-shadow var(--sh-motion) ease;
}
.sh-brand:hover .sh-brand__mark {
  transform: translateY(-1px) rotate(-3deg) scale(1.04);
  box-shadow: 0 6px 16px -8px rgba(var(--v-theme-primary), 0.7);
}
.sh-brand__id {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.sh-brand__name {
  flex: 0 1 auto;
  min-width: 0;
  font-size: 13.5px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--sh-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* The product qualifier beside the brand — a hairline keeps the two readable
   as "Metaship" AND "CRM" rather than one run-on name. */
.sh-brand__sub {
  flex: 0 1 auto;
  min-width: 0;
  padding-left: 8px;
  border-left: 1px solid var(--sh-hairline);
  font-size: 11.5px;
  font-weight: 600;
  color: var(--sh-text-60);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sh-collapse,
.sh-nav-toggle {
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  border-radius: var(--sh-radius);
  background: transparent;
  cursor: pointer;
  color: var(--sh-text-45);
  transition:
    background 150ms ease,
    color 150ms ease;
}
.sh-nav-toggle {
  /* The primary way into navigation on a phone — full-strength ink, and a
     touch target rather than a 26px chevron. */
  width: 36px;
  height: 36px;
  margin-left: -4px;
  color: var(--sh-text);
}
.sh-collapse:hover,
.sh-nav-toggle:hover {
  background: var(--sh-hover);
  color: var(--sh-text);
}
.sh-collapse:active,
.sh-nav-toggle:active {
  transform: scale(0.92);
}
.sh-collapse__icon {
  transition: transform var(--sh-motion-slow) var(--sh-ease-spring);
}
.sh-collapse--rail .sh-collapse__icon {
  transform: rotate(180deg);
}
/* The chevron leans the way the click will move things. */
.sh-collapse:hover .sh-collapse__icon {
  transform: translateX(-2px);
}
.sh-collapse--rail:hover .sh-collapse__icon {
  transform: rotate(180deg) translateX(-2px);
}

/* ── Module actions ────────────────────────────────────────────────────
   Whatever a module teleports in inherits the bar's ink; a module's own
   theme tokens are not defined out here (its <v-app> is elsewhere in the
   document), so an `rgb(var(--v-theme-…))` colour in the module falls
   back to this inherited value rather than to black. */
.sh-actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 2px;
  color: var(--sh-text-60);
}
.sh-actions:empty {
  display: none;
}

/* ── Narrow screens ───────────────────────────────────────────────────── */
@media (max-width: 959.98px) {
  .standalone-topbar :deep(.v-toolbar__content) {
    padding-left: 8px;
    padding-right: 4px;
    gap: 2px;
  }
}
@media (max-width: 420px) {
  /* Below this the product qualifier and the brand word compete for the same
     ~90px. The MARK is the identity at this size; the name follows it. */
  .sh-brand__sub {
    display: none;
  }
  .sh-brand__name {
    max-width: 96px;
  }
}
</style>
